// Two-brand split (business plan, locked 2026-04-29): guests get the
// destination site (<city>.vacations), hosts get the platform site
// (stay.vacations). One repo, one Netlify deploy, both domains aliased to it;
// the SPA branches on hostname at the top of App.jsx.
//
// Brand resolution order:
//   1. Real hostnames — *.stay.vacations wins over <city>.vacations so
//      app.stay.vacations lands on the platform side.
//   2. Dev/preview override — ?site=stay|charleston (persisted for the SPA
//      session so client-side nav doesn't lose it on Netlify previews and
//      localhost, where the hostname carries no brand).
//   3. VITE_SITE_BRAND env (lets a second Netlify site build the platform
//      brand from the same repo later, if we ever split deploys).
//   4. Default: the city brand. The guest site is the safe fallback.

const OVERRIDE_KEY = 'sv.siteBrand';

// Registry of city sites. `status` is honest — only flip to 'live' when the
// brief for that city actually publishes daily.
export const CITIES = {
  charleston: {
    slug: 'charleston',
    name: 'Charleston',
    state: 'SC',
    domain: 'charleston.vacations',
    status: 'live',
    tagline: 'A daily conditions brief for one Charleston day, published by 6:30 AM ET.'
  },
  savannah: {
    slug: 'savannah',
    name: 'Savannah',
    state: 'GA',
    domain: 'savannah.vacations',
    status: 'next',
    tagline: 'The second city. Opens after the Charleston founding cohort fills.'
  }
};

export const PLATFORM_DOMAIN = 'stay.vacations';

// This deploy's own city (Phase 1: single-city repo). When the Savannah repo
// or multi-city build exists, this becomes per-deploy config.
export const HOME_CITY = 'charleston';

function resolveBrand() {
  if (typeof window === 'undefined') return { brand: 'city', city: HOME_CITY };

  const host = window.location.hostname.toLowerCase();
  if (host === PLATFORM_DOMAIN || host.endsWith('.' + PLATFORM_DOMAIN)) {
    return { brand: 'stay', city: null };
  }
  for (const city of Object.values(CITIES)) {
    if (host === city.domain || host.endsWith('.' + city.domain)) {
      return { brand: 'city', city: city.slug };
    }
  }

  // No brand in the hostname: localhost or a Netlify preview URL.
  const param = new URLSearchParams(window.location.search).get('site');
  // Unguarded, these three throw in a private window with site data blocked,
  // and this runs before anything is painted, so the whole preview would be a
  // blank page. Only preview and localhost hosts reach here, but the fix is a
  // try/catch and the rest of the codebase already wraps every storage access.
  let override = null;
  try {
    if (param === 'stay') window.sessionStorage.setItem(OVERRIDE_KEY, 'stay');
    if (param === 'city' || param === HOME_CITY) window.sessionStorage.removeItem(OVERRIDE_KEY);
    override = window.sessionStorage.getItem(OVERRIDE_KEY);
  } catch {
    // Storage unavailable: fall back to the query param for this page load.
    override = param === 'stay' ? 'stay' : null;
  }
  if (override === 'stay') {
    return { brand: 'stay', city: null, preview: true };
  }
  if (import.meta.env.VITE_SITE_BRAND === 'stay') {
    return { brand: 'stay', city: null };
  }
  return { brand: 'city', city: HOME_CITY };
}

let cached = null;
export function getSite() {
  if (!cached) cached = resolveBrand();
  return cached;
}

// The host every canonical, og:url and JSON-LD url names: the brand's own
// domain, never the host the page happened to be fetched from. The same build
// answers on the Netlify subdomain and on deploy previews, and a canonical read
// from window.location named whichever of those a crawler came in on.
export function canonicalOrigin() {
  const site = getSite();
  const domain = site.brand === 'stay' ? PLATFORM_DOMAIN : CITIES[site.city || HOME_CITY].domain;
  return `https://${domain}`;
}

// The canonical URL for a path: the brand origin plus the path without a
// trailing slash, because the site's URLs have none ("/" stays "/"). Google
// read the runtime canonical over the prerendered one, so when Netlify served
// /x at /x/ it took /x/ as the declared URL (GSC review 2026-10-01).
export function canonicalUrl(pathname = window.location.pathname) {
  return canonicalOrigin() + (pathname.replace(/\/+$/, '') || '/');
}
